import { HierarchyDetails } from '@components/UI-Components/HierarchyWiseItems'
import { Column } from '@components/UI-Components/HierarchyWiseItemsTable/models/column'
import { VIPClientType } from '@modules/common/models/enums/VIPClientType'
import {
  CATEGORY_INTERNAL_SEPERATOR,
  CLIENT_ORDER_LEVEL_GROUP_ATTR,
  DOOR_ORDER_LEVEL_GROUP_ATTR,
  HIERARCHY_ROOT_ID,
  KPI_TOPBAR_METRIC_ATTRIBUTES,
  METRIC_ATTRIBUTES_FOR_DOOR_AND_SIZE_QUANTITY,
  METRIC_ATTRIBUTES_FOR_PRODUCT_QUANTITY
} from '@modules/order/constants/uiConstants'
import { nav } from '@stores/nav'
import PaginatedTableHierarchyItemsUIStore from '@stores/paginated-table-hierarchy-items/PaginatedTableHierarchyItemsUIStore'
import { productCurveStore } from '@stores/productCurvesStore'
import { getEnumValue } from '@utils/get-enum-value'
import { extractIndexFromId } from '@utils/index-from-id'
import formatCurrencyValueByLocale from '@utils/format-currency-value-by-locale'
import formatNumberByLocale from '@utils/format-number-by-locale'
import {
  difference,
  differenceBy,
  flatMapDeep,
  isEmpty,
  isNil,
  keys,
  mapValues,
  reduce
} from 'lodash'
import find from 'lodash/find'
import map from 'lodash/map'
import filter from 'lodash/filter'
import omit from 'lodash/omit'
import sum from 'lodash/sum'
import values from 'lodash/values'
import has from 'lodash/has'
import { action, computed, observable, toJS, transaction } from 'mobx'
import { computedFn } from 'mobx-utils'
import { QuantityType } from '@components/UI-Components/QuantityType/constants/QuantityType'
import { stores } from '@stores'
import { getChannelName } from '@utils/get-channel-name'
import { EnhancedListDataStore } from './EnhancedListDataStore'
import OrderCellActiveCellItemStore from '@modules/order/stores/view/List/OrderCellActiveCellItemStore'
import { ListTableView } from '@modules/order/stores/view/List/ListViewTableService'
import { orderKPIGlobalTopBarStore } from '@modules/order/stores/OrderKPIGlobalTopBarStore'
import EnhancedListViewTableService from './EnhancedListViewTableService'
import { OrderType } from '@api/common'
import {
  OrderTemplatePreferences,
  orderTemplatePreferencesStore
} from '@modules/order/stores/order/OrderTemplatePreferencesStore'
import { omitSingle } from '@utils/array-utils'
import { ViewModes } from '@modules/order/constants/views'
import { getEnhancedImageURL } from '@modules/showroom/productCollection/hooks/getEnhancedImageURL'
import { ProductSizeQuantityMap, QuantityPatchMap } from '../../types'

export interface ISize {
  id: string
  name: string
  sizeCode?: number
  isInactive?: boolean
}

interface TemplatePasteType {
  DOOR_BOOKING: boolean
  QUANTITIES: boolean
}
export class EnhancedListUIStore<
  T extends EnhancedListDataStore
> extends PaginatedTableHierarchyItemsUIStore<T> {
  activeItemStore = new OrderCellActiveCellItemStore()

  @observable isValueSelected = false
  @observable showProductSizes: {
    [productId: string]: boolean
  } = {}
  @observable currentViewConfig = null

  // Both variables are used for handling the popup open/close state at each product level.
  @observable splitActionsProductId = ''
  @observable rollUpActionsProductId = ''

  @action setSplitActionsProductId = id => {
    this.splitActionsProductId = id
  }

  @action setRollUpActionsProductId = id => {
    this.rollUpActionsProductId = id
  }

  @action
  setIsValueSelected = (event, checked) => {
    this.isValueSelected = event?.target?.checked || checked
  }

  tableService: any = new EnhancedListViewTableService(
    nav.queryParams.subSection ||
      (nav.queryParams.clientType === getEnumValue(VIPClientType.Internal)
        ? ListTableView.Summary
        : ListTableView.Quantity),
    nav.queryParams.clientType === getEnumValue(VIPClientType.Internal)
      ? [ListTableView.Summary, ListTableView.Quantity]
      : [ListTableView.Quantity],
    {}
  )

  @action
  init() {
    // This method is used to save topbar selections across views
    this.restoreOrdersTopbarSelections()
    super.init()
  }

  @computed get hierarchyDetails(): HierarchyDetails {
    if (!this.dataStore.heirarchy?.length) {
      return {
        [`${HIERARCHY_ROOT_ID}`]: {
          children: [],
          startIndex: null,
          endIndex: null,
          depth: -1
        }
      }
    }
    // omit finished key from heirarchy details
    const heirarchy: any = omit(
      this.generateTableDataRecursively(this.dataStore.heirarchy, '', 0, 0, 0),
      'finished'
    )
    return {
      [HIERARCHY_ROOT_ID]: {
        children: map(this.dataStore.heirarchy, 'name')
      },
      ...heirarchy
    }
  }

  @action
  setCurrentViewConfig = () => {
    const {
      nav: {
        queryParams: { clientType }
      },
      productConfiguration: { getViewProductCardAttributes }
    } = stores
    const channelName = getChannelName(clientType)
    this.currentViewConfig = getViewProductCardAttributes(
      channelName,
      this.dataStore.currentViewName
    )
  }

  @action
  prepareViewData = () => {
    this.dataStore.fetchAndPrepareFilterAttributesData(this.dataStore.currentViewName)
    this.dataStore.cnfGroupByUIStore.setGroupByAttributes(this.dataStore.currentViewName)
    this.dataStore.enhancedSortStore.setSortAttributesList(this.dataStore.currentViewName)
  }

  handleInitialAllocationToggle = isChecked => {
    const {
      nav: { updateQueryParams }
    } = stores
    updateQueryParams(
      {
        viewMode: isChecked ? ViewModes.InitialAllocation : undefined
      },
      'push'
    )
  }

  /**
   * Get Doorwise total kpi's to show in the header rows of quantity view.
   */
  getHeaderDoorKpis = computedFn(
    (doorId: string, quantityType: string, currency?: string) => {
      const {
        nav: {
          queryParams: { clientId }
        }
      } = stores
      const fieldType = QuantityType.Size === quantityType ? 'Sized' : 'Door'
      const fieldTypeToMap = QuantityType.Size === quantityType ? 'Size' : 'Door'
      const doorKpis = find(this.dataStore?.totalDoorKPIs, door => door?.id === doorId)
      const orderTopbarOTB = find(
        orderKPIGlobalTopBarStore?.enhancedOTBData?.children,
        otb => otb?.id === clientId
      )
      const otbDataByDoorId = find(
        orderTopbarOTB?.children,
        doorOTB => doorOTB?.id === doorId
      )
      const buyerCurrency =
        otbDataByDoorId?.buyerCurrency ?? otbDataByDoorId?.globalCurrency ?? ''
      const buyerBudgetValue = otbDataByDoorId?.buyerBudgetValue ?? 0
      const doorKPI = {
        doorOTB: buyerCurrency
          ? formatCurrencyValueByLocale(buyerCurrency, buyerBudgetValue)
          : formatNumberByLocale(buyerBudgetValue, 0, 2),
        [`total${fieldType}Value`]: formatCurrencyValueByLocale(
          currency ?? '',
          doorKpis?.[`total${fieldTypeToMap}Value`] ?? 0
        ),
        [`total${fieldType}Quantity`]: `${formatNumberByLocale(
          doorKpis?.[`total${fieldTypeToMap}Quantity`] ?? 0
        )}`,
        numberOfACs: `${formatNumberByLocale(doorKpis?.totalProducts ?? 0)}`
      }
      return doorKPI
    }
  )

  getOrderHierarchyKPIs = (heirarchyGroups: string, heirarchyData: any): any => {
    const doorKPIs = {}
    const heirarchyGroupsIds = heirarchyGroups?.split(CATEGORY_INTERNAL_SEPERATOR)
    this.getDoorLevelValues(
      find(heirarchyData, doorOrder => doorOrder?.id === heirarchyGroupsIds[0]),
      heirarchyGroupsIds,
      doorKPIs
    )
    return doorKPIs?.[heirarchyGroupsIds.pop()]
  }

  getDoorOrderHierarchyKPIs = (
    doorOrderId: string,
    heirarchyGroups: string,
    totalDoorKPIs: any
  ): any => {
    const doorKPIs = {}
    const heirarchyGroupsIds = heirarchyGroups?.split(CATEGORY_INTERNAL_SEPERATOR)
    const doorOrders =
      find(totalDoorKPIs, door => door?.id === doorOrderId)?.children || {}
    const doorOrderKPIs = find(
      doorOrders,
      doorOrder => doorOrder?.id === heirarchyGroupsIds[0]
    )
    this.getDoorLevelValues(doorOrderKPIs, heirarchyGroupsIds, doorKPIs)
    return doorKPIs?.[heirarchyGroupsIds.pop()]
  }

  getDoorLevelValues = (doorOrderKPIs, heirarchyGroupsIds, doorKPIs) => {
    heirarchyGroupsIds.forEach((activity, index) => {
      const heirarchyLevelArr = heirarchyGroupsIds.slice(0, index + 1)
      if (
        activity === doorOrderKPIs?.id &&
        heirarchyLevelArr.every(level => doorOrderKPIs?.parentGroupNames?.includes(level))
      ) {
        doorKPIs[activity] = doorOrderKPIs
        return
      } else {
        if (doorOrderKPIs?.children) {
          doorOrderKPIs?.children.forEach(doorOrder => {
            this.getDoorLevelValues(doorOrder, heirarchyGroupsIds, doorKPIs)
          })
        }
      }
    })
  }

  getChildrenData = door => {
    if (!door.children || !door.children.length) {
      return door
    }
    return [door, flatMapDeep(door.children, this.getChildrenData)]
  }

  getGroupObject = computedFn((name, depth, children, totalItems) => ({
    name,
    isLeaf: false,
    depth: depth,
    children,
    totalItems
  }))

  getChildObject = computedFn(
    (name, depth, children, startIndex, endIndex, totalItems) => ({
      name,
      isLeaf: false,
      depth: depth,
      children,
      startIndex,
      endIndex,
      totalItems
    })
  )

  @action
  updateProductQuantity = (
    productId: string,
    newValue: number,
    selectedGrading: any,
    rollUp: boolean = false,
    rollUpSizeToDoor?: boolean,
    rollUpDoorToProduct?: boolean
  ) => {
    const {
      queryParams: { orderType, clientType, subSection }
    } = nav
    const product = this.dataStore.itemIndexObjectStore.idObjectMap.get(productId)

    const isSummaryListView =
      clientType === getEnumValue(VIPClientType.Internal) &&
      (subSection === ListTableView.Summary || !subSection)
    const payload = {
      productQuantity: newValue,
      gradingAttributes: selectedGrading ?? {},
      rollUp,
      aggregations: {
        heirarchy: {
          metricAttributes: rollUp
            ? [
                ...METRIC_ATTRIBUTES_FOR_PRODUCT_QUANTITY,
                ...METRIC_ATTRIBUTES_FOR_DOOR_AND_SIZE_QUANTITY,
                ...this.dataStore.avgDepthAndTotalStoresAttributes
              ]
            : [
                ...METRIC_ATTRIBUTES_FOR_PRODUCT_QUANTITY,
                ...this.dataStore.avgDepthAndTotalStoresAttributes,
                ...(isSummaryListView
                  ? ['averageDepth', 'salesQuantity', 'averageWeeklySales']
                  : [])
              ],
          ...this.dataStore.getPayloadForUpdatedPageLevelHeirarchy(product)
        },
        orderTopbarKPIs: {
          metricAttributes: rollUp
            ? KPI_TOPBAR_METRIC_ATTRIBUTES
            : METRIC_ATTRIBUTES_FOR_PRODUCT_QUANTITY,
          groupBy: [
            orderType === OrderType.Door
              ? DOOR_ORDER_LEVEL_GROUP_ATTR
              : CLIENT_ORDER_LEVEL_GROUP_ATTR
          ]
        },
        ...(rollUp
          ? {
              totalDoorHierarchyKPIs: {
                metricAttributes: rollUp
                  ? [
                      ...METRIC_ATTRIBUTES_FOR_PRODUCT_QUANTITY,
                      ...METRIC_ATTRIBUTES_FOR_DOOR_AND_SIZE_QUANTITY
                    ]
                  : METRIC_ATTRIBUTES_FOR_PRODUCT_QUANTITY,
                ...this.dataStore.getPayloadForUpdatedDoorLevelHeirarchy()
              }
            }
          : {}),
        ...this.dataStore.getPayloadForSingleProductAggregation(productId)
      },
      ...(rollUpSizeToDoor !== undefined && rollUpDoorToProduct !== undefined
        ? {
            rollUpSizeToDoor,
            rollUpDoorToProduct
          }
        : {})
    }
    return this.dataStore.updateProductQuantity(productId, payload, isSummaryListView)
  }

  @action
  rollUpAllQuantities = (rollUpSizeToDoor: boolean, rollUpDoorToProduct: boolean) => {
    this.dataStore.rollUpAllQuantities(rollUpSizeToDoor, rollUpDoorToProduct)
  }

  @action
  splitAllQuantities = (
    splitProductToDoors: boolean = false,
    splitDoorsToSizes: boolean = false,
    curveTypeToBeUsed: string
  ) => {
    const {
      nav: {
        queryParams: { orderType }
      }
    } = stores
    const buyingSessionProductIds = this.dataStore.products
    const payload = {
      buyingSessionProductIds,
      aggregations: {
        heirarchy: {
          metricAttributes: [
            ...METRIC_ATTRIBUTES_FOR_PRODUCT_QUANTITY,
            ...METRIC_ATTRIBUTES_FOR_DOOR_AND_SIZE_QUANTITY
          ],
          ...this.dataStore.getPayloadForUpdatedPageLevelHeirarchy({})
        },
        orderTopbarKPIs: {
          metricAttributes: KPI_TOPBAR_METRIC_ATTRIBUTES,
          groupBy: [
            orderType === OrderType.Door
              ? DOOR_ORDER_LEVEL_GROUP_ATTR
              : CLIENT_ORDER_LEVEL_GROUP_ATTR
          ]
        },
        totalDoorHierarchyKPIs: {
          metricAttributes: [
            ...METRIC_ATTRIBUTES_FOR_PRODUCT_QUANTITY,
            ...METRIC_ATTRIBUTES_FOR_DOOR_AND_SIZE_QUANTITY
          ],
          ...this.dataStore.getPayloadForUpdatedDoorLevelHeirarchy()
        }
      },
      splitProductToDoors,
      splitDoorsToSizes
    }

    this.dataStore.splitAllQuantities(payload, curveTypeToBeUsed)
  }
  // *********** Quantity Sub Section ***********
  @action
  hideShowProductSizes = (flag: boolean, productId: string) => {
    this.showProductSizes = {
      ...this.showProductSizes,
      [productId]: flag
    }
  }

  getBookedDoors = computedFn((productId: string, doorOrders: any) => {
    const product = this.dataStore.itemIndexObjectStore.idObjectMap.get(productId)
    const bookedDoorOrderIds = filter(
      Object.keys(product?.doorOrderProducts || {}),
      doorOrderId =>
        !find(doorOrders, doorOrder => doorOrder?.door?.id === doorOrderId)?.deleted
    )
    return bookedDoorOrderIds
  })

  @computed get areObjectsDataLoading() {
    return this.dataStore.itemIndexObjectStore.objectLoadingProgress.every(map =>
      map.get('LOADING')
    )
  }

  getSumOfDraftQuantityOfBookedDoors = computedFn((productId: string) => {
    const product = this.dataStore.itemIndexObjectStore.idObjectMap.get(productId)
    const value = reduce(
      product?.doorOrderProducts,
      (acc, door) => acc + (door?.doorOrderProductDoorQuantity || 0),
      0
    )
    return value
  })

  getSizes = computedFn((productId: string): Array<ISize> => {
    const product = this.dataStore.itemIndexObjectStore.idObjectMap.get(productId)
    const sizes = (
      product?.['original-sizes-id']?.reduce((acc, size, index) => {
        acc?.push({
          id: size,
          name: product?.['original-sizes-name'][index],
          sizeCode: product?.['original-sizes-sizeCode'][index]
        })
        return acc
      }, []) || []
    )
      .concat(
        (product?.inactiveSizes || []).map(inactiveSize => {
          return { ...inactiveSize, isInactive: true }
        })
      )
      .sort((sizeA, sizeB) => sizeA.sizeCode - sizeB.sizeCode)
    return sizes
  })

  getProductCurves = computedFn((productId: string) => {
    const {
      queryParams: { clientId, buyingSessionGroupId }
    } = nav
    const curves = productCurveStore.getCurvesForProduct(
      productId,
      clientId,
      buyingSessionGroupId
    )
    return {
      sizeCurve: curves?.sizeCurve ?? {},
      doorCurve: curves?.doorCurve ?? {}
    }
  })

  getProductSizeCurve = computedFn((productId: string, doorId: string) => {
    const sizeCurves = { ...(this.getProductCurves(productId).sizeCurve ?? {}) }
    const sizes = this.getSizes(productId)
    // filter Size curve by active sizes
    const calculatedSizeCurves = sizes?.reduce(
      (acc, { name, id, isInactive }) => {
        const hasCurve = sizeCurves?.curves?.hasOwnProperty(name) ?? false
        const curveValue = sizeCurves?.curves?.[name]

        return {
          ...acc,
          curve: {
            ...(acc.curve || {}),
            ...(hasCurve && !isInactive ? { [name]: curveValue } : {})
          }
        }
      },
      { curve: {}, doors: {} }
    )

    calculatedSizeCurves.doors = Object.keys(sizeCurves?.doors || {}).reduce(
      (acc, doorId) => ({
        ...acc,
        [doorId]: sizes.reduce((innerAcc, { name, isInactive }) => {
          const hasDoorCurve = sizeCurves?.doors?.[doorId]?.hasOwnProperty(name) ?? false
          const doorCurveValue = sizeCurves?.doors?.[doorId]?.[name]

          return {
            ...innerAcc,
            ...(hasDoorCurve && !isInactive ? { [name]: doorCurveValue } : {})
          }
        }, {})
      }),
      {}
    )

    const productCurve = { ...calculatedSizeCurves.curve }
    const doorCurve = calculatedSizeCurves?.doors?.[doorId] || {}

    const curves = !isEmpty(doorCurve) ? doorCurve : productCurve
    const curveTotal = sum(values(curves))
    if (curveTotal) {
      return Object.keys(curves).reduce(
        (acc, key) => ({ ...acc, [key]: curves[key] / curveTotal }),
        {}
      )
    }
    return {}
  })

  getProductDoorCurveByGrading = computedFn((productId: string, activeDoorIds: any) => {
    const product = this.dataStore.itemIndexObjectStore.idObjectMap.get(productId)
    const { clientOrderProductGradingAttributes = {} } = product
    const doorCurves = { ...(this.getProductCurves(productId).doorCurve ?? {}) }
    const grade = clientOrderProductGradingAttributes?.grade ?? ''
    const doorCurveByGrading = grade
      ? doorCurves?.curves?.[grade] ?? doorCurves?.curves
      : doorCurves?.curves ?? {}
    const calculatedDoorCurves = activeDoorIds.reduce(
      (acc, doorId) => {
        return {
          ...acc,
          curve: {
            ...(acc?.curve || {}),
            ...(has(doorCurveByGrading, doorId)
              ? {
                  [doorId]: doorCurveByGrading?.[doorId]
                }
              : {})
          }
        }
      },
      { curve: {}, doors: {} }
    )
    const curves = { ...calculatedDoorCurves.curve }
    const curveTotal = sum(values(curves))
    if (curveTotal) {
      return Object.keys(curves).reduce(
        (acc, key) => ({ ...acc, [key]: curves[key] / curveTotal }),
        {}
      )
    }
    return {}
  })

  getQuantitiesBySizeCurve = (
    inputValue: number,
    sizes: any[],
    curves: any,
    oldSizeValues: any
  ) => {
    let sizeQuantites: QuantityPatchMap = {}
    sizes.forEach(size => {
      const curveWeight = curves[size.name]
      let sizeHasCurveWeight = true
      if (!curves.hasOwnProperty(size.name)) {
        sizeHasCurveWeight = false
      }
      if (curveWeight) {
        sizeQuantites[size.id] = this.getQuantityByCurve(inputValue, curveWeight)
      } else {
        if (sizeHasCurveWeight) {
          sizeQuantites[size.id] = 0
        } else {
          sizeQuantites[size.id] = oldSizeValues[size.id]?.quantity || 0
        }
      }
    }, {})
    return sizeQuantites
  }

  getQuantityByCurve = (value: number, curve: number) => {
    return Math.round(value * curve)
  }

  getDoorQuantitiesByDoorCurve = (
    inputValue: number,
    doors: any,
    curves: any,
    quantityAttributeName: string
  ) => {
    return Object.keys(doors || {}).reduce((acc, doorId) => {
      const curve = curves[doorId]
      return {
        ...acc,
        ...(curve
          ? { [doorId]: this.getQuantityByCurve(inputValue, curve) }
          : {
              [doorId]: curve === 0 ? curve : doors[doorId]?.[quantityAttributeName] || 0
            })
      }
    }, {})
  }

  @action
  handleDoorQtyUpdate = (
    productId: string,
    doorId: string,
    newValue: number,
    doorOrderId: string,
    orderType: OrderType,
    captureSizeEnabled: boolean
  ) => {
    return this.updateDoorQuantities(
      productId,
      doorId,
      doorOrderId,
      orderType === OrderType.Door,
      captureSizeEnabled,
      newValue
    )
  }

  @action
  handleDoorSizeQtyUpdate = (
    productId: string,
    doorOrderId: string,
    doorId: string,
    newValue: number,
    sizeId: string,
    orderType: OrderType,
    captureSizeEnabled: boolean
  ) => {
    return this.updateDoorQuantities(
      productId,
      doorId,
      doorOrderId,
      orderType === OrderType.Door,
      captureSizeEnabled,
      undefined,
      { [sizeId]: newValue }
    )
  }

  @action
  splitOrRollupDoorQuantities = (
    productId: string,
    doorId: string,
    doorOrderId: string,
    orderType: OrderType,
    isCaptureSizeEnabled: boolean,
    isSplitOperation: boolean
  ) => {
    const isDoorOrder = orderType === OrderType.Door
    const product = toJS(this.dataStore.itemIndexObjectStore.idObjectMap.get(productId))
    const doorData = isDoorOrder ? product : product.doorOrderProducts[doorId]
    let draftQuantity: number
    const productSQs: ProductSizeQuantityMap = doorData.doorOrderSizeQuantities ?? {}
    let patchMap: QuantityPatchMap | undefined

    if (isSplitOperation) {
      draftQuantity = doorData.doorOrderProductDoorQuantity

      // Split: Apply curves to distribute size quantities
      patchMap = this.getQuantitiesBySizeCurve(
        draftQuantity,
        this.getSizes(productId),
        this.getProductSizeCurve(productId, doorId),
        productSQs
      )
    } else {
      // Rollup: sum existing size quantities to get new draftQuantity
      draftQuantity = reduce(productSQs, (acc, entry) => acc + +entry.quantity, 0)
    }

    return this.updateDoorQuantities(
      productId,
      doorId,
      doorOrderId,
      isDoorOrder,
      isCaptureSizeEnabled,
      draftQuantity,
      patchMap
    )
  }

  @action
  bookUnbookDoors = (
    clientOrderId,
    productId,
    doorOrderIds,
    isBook,
    doorIds?,
    isAll?
  ) => {
    return this.dataStore.bookUnBookDoors(
      clientOrderId,
      productId,
      doorOrderIds,
      isBook,
      false,
      doorIds,
      isAll
    )
  }

  @action
  bulkUpdateDraftQuantity = (
    summary,
    productId,
    value,
    splitProductToDoors = false,
    splitDoorsToSizes = false
  ) => {
    const {
      queryParams: { orderType }
    } = nav
    const product = this.dataStore.itemIndexObjectStore.idObjectMap.get(productId)
    const { doorOrderProducts = {} } = product
    const doorIds = Object.keys(doorOrderProducts)
    const activeDoorIds = filter(
      doorIds,
      doorId =>
        !find(summary?.doorOrders, doorOrder => doorOrder?.door?.id === doorId)?.deleted
    )
    //Door Curves
    // Skip processing door curves when splitDoorsToSizes operation is selected.
    const productDoorCurves = !splitDoorsToSizes
      ? this.getProductDoorCurveByGrading(productId, activeDoorIds)
      : {}
    const doorQuantitiesByCurve = this.getDoorQuantitiesByDoorCurve(
      value,
      doorOrderProducts,
      productDoorCurves,
      'doorOrderProductDoorQuantity'
    )
    const sizes = this.getSizes(productId)
    const isCaptureSizeEnabled =
      summary?.client?.captureSize || summary?.clientOrder?.client?.captureSize
    const payload = {
      bulkDoorOrderQuantities: activeDoorIds.map(doorId => {
        //Size Curves
        // Skip processing door curves when splitProductToDoors operation is selected
        const doorSizeCurves = !splitProductToDoors
          ? this.getProductSizeCurve(productId, doorOrderProducts[doorId].doorId)
          : {}
        const sizeQuantities = this.getQuantitiesBySizeCurve(
          doorQuantitiesByCurve[doorId] ?? 0,
          sizes,
          doorSizeCurves,
          doorOrderProducts[doorId].doorOrderSizeQuantities || {}
        )
        return {
          doorOrderId: find(
            summary?.doorOrders,
            doorOrder => doorOrder?.door?.id === doorId
          )?.id,
          draftQuantity: doorQuantitiesByCurve[doorId] ?? 0,
          sizeQuantity:
            isEmpty(sizeQuantities) || !isCaptureSizeEnabled
              ? doorQuantitiesByCurve[doorId] ?? 0
              : Object.values(sizeQuantities).reduce((sizeQty, acc) => {
                  return +acc + +sizeQty
                }, 0),
          sizeQuantities: this.stripZeroQuantities(sizeQuantities)
        }
      }),
      aggregations: {
        heirarchy: {
          metricAttributes: METRIC_ATTRIBUTES_FOR_DOOR_AND_SIZE_QUANTITY,
          ...this.dataStore.getPayloadForUpdatedPageLevelHeirarchy(product)
        },
        totalDoorHierarchyKPIs: {
          metricAttributes: METRIC_ATTRIBUTES_FOR_DOOR_AND_SIZE_QUANTITY,
          ...this.dataStore.getPayloadForUpdatedDoorLevelHeirarchy()
        },
        orderTopbarKPIs: {
          metricAttributes: METRIC_ATTRIBUTES_FOR_DOOR_AND_SIZE_QUANTITY,
          groupBy: [
            orderType === OrderType.Door
              ? DOOR_ORDER_LEVEL_GROUP_ATTR
              : CLIENT_ORDER_LEVEL_GROUP_ATTR
          ]
        },
        ...this.dataStore.getPayloadForSingleProductAggregation(
          productId,
          METRIC_ATTRIBUTES_FOR_DOOR_AND_SIZE_QUANTITY
        )
      }
    }
    return this.dataStore.bulkUpdateDraftQuantity(summary, productId, payload)
  }

  @action
  updateComment = (productId: string, value: string, commentType: string) => {
    this.dataStore.updateComment(productId, value, commentType)
  }

  getActiveColumn = (
    rowIndex: number,
    colIndex: number,
    visibleColumns: Column[]
  ): number => {
    do {
      const column: Column =
        this.elementRefStore.tableRef.getColumnByVisibleIndex(colIndex)
      const rowData = this.dataStore.itemIndexObjectStore.indexObjectMap.get(rowIndex)
      if (rowData?.doorOrderProducts?.[column.field]) {
        break
      }
      colIndex++
    } while (colIndex !== visibleColumns.length)
    return colIndex
  }

  getPrevActiveColumn = (rowIndex: number, colIndex: number): number => {
    do {
      const column: Column =
        this.elementRefStore.tableRef.getColumnByVisibleIndex(colIndex)
      if (!column) {
        return -1
      }
      const rowData = this.dataStore.itemIndexObjectStore.indexObjectMap.get(rowIndex)
      if (rowData?.doorOrderProducts?.[column.field]) {
        return colIndex
      }
      colIndex--
    } while (colIndex !== 0)
    return -1
  }

  @action
  navigateToNextEditableCell = (curRowIndex?: number, curColIndex?: number) => {
    const visibleColumns = this.elementRefStore.tableRef.flattenedVisibleColumns
    const lastRowIndex = this.dataStore.itemIndexObjectStore.objects.length
    const rowIndexToStartFrom = lastRowIndex === curRowIndex ? 0 : curRowIndex
    if (visibleColumns.length) {
      if (curColIndex !== visibleColumns.length - 1) {
        const colIndex = this.getActiveColumn(
          rowIndexToStartFrom,
          curColIndex + 1,
          visibleColumns
        )
        // Check if any cell in next columns in current row is editable
        if (colIndex !== visibleColumns.length) {
          this.activeItemStore.setSizeIndex(-1)
          this.navigateToCell(rowIndexToStartFrom, colIndex)
          return
        }
      }
      // Means no cell is editable in current row
      let rowIndex = rowIndexToStartFrom + 1
      while (rowIndex !== lastRowIndex) {
        const colIndex = this.getActiveColumn(rowIndex, 0, visibleColumns)
        // Check if any cell in next columns in current row is editable
        if (colIndex !== visibleColumns.length) {
          this.activeItemStore.setSizeIndex(-1)
          this.navigateToCell(rowIndex, colIndex)
          return
        }
        rowIndex++
      }
      // It would be last row
      this.navigateToNextEditableCell(0, 0)
    }
  }

  @action
  navigateToPrevAvailableCell = (curRowIndex?: number, curColIndex?: number) => {
    const visibleColumns = this.elementRefStore.tableRef.flattenedVisibleColumns
    const rowIndexToStartFrom = curRowIndex
    if (visibleColumns.length) {
      const colIndex = this.getPrevActiveColumn(rowIndexToStartFrom, curColIndex - 1)
      // Check if any cell in next columns in current row is editable
      if (colIndex !== -1) {
        this.activeItemStore.setSizeIndex(-1)
        this.navigateToCell(rowIndexToStartFrom, colIndex)
        return
      }

      if (rowIndexToStartFrom === 0) {
        return
      }
      // Means no cell is editable in current row
      let rowIndex = rowIndexToStartFrom - 1
      do {
        const colIndex = this.getPrevActiveColumn(rowIndex, visibleColumns.length - 1)
        // Check if any cell in next columns in current row is editable
        if (colIndex !== -1) {
          this.navigateToCell(rowIndex, colIndex)
          this.activeItemStore.setSizeIndex(-1)
          return
        }
        rowIndex--
      } while (rowIndex !== -1)
    }
  }

  @computed get copiedTemplateQuantities() {
    return orderTemplatePreferencesStore.templates?.clipboard?.quantity?.quantities
  }

  @action
  refresh = async () => {
    if (this.elementRefStore.ref?.getScrollTop) {
      const scrollPosition = this.elementRefStore.ref.getScrollTop()
      const scrolledNodeId = this.scrollPositionStore.scrolledNode?.id
      if (!scrolledNodeId) {
        return
      }
      const [, index] = extractIndexFromId(scrolledNodeId)
      transaction(async () => {
        await this.dataStore.fetchHierarchy()
        this.dataStore.itemIndexObjectStore.clearAll()
        if (this.dataStore.heirarchy.length) {
          this.activeItemStore.setIndex(index)
          this.scrollPositionStore.scrollToNode(scrolledNodeId)
          this.elementRefStore.ref.setScrollTop(scrollPosition)
        }
      })
    }
  }

  /** Shared helper to update one door's size quantities, total, and draft quantity */
  updateDoorQuantities = (
    productId: string,
    doorId: string,
    doorOrderId: string,
    isDoorOrder = false,
    isCaptureSizeEnabled = false,
    draftQuantity: number | undefined = undefined,
    modifiedSizeQuantities: QuantityPatchMap = {} // Must include zeros to overwrite values. Zeroes are removed prior to sending.
  ) => {
    const product = toJS(this.dataStore.itemIndexObjectStore.idObjectMap.get(productId))
    const info = isDoorOrder ? product : product.doorOrderProducts[doorId] ?? {}
    const oldTotalDoorQuantity = info?.doorOrderProductDoorQuantity ?? 0
    const oldTotalSizeQuantity = info?.doorOrderProductSizeQuantity ?? 0
    const doorQuantity = draftQuantity ?? oldTotalDoorQuantity

    // Overlay patch quantities into product
    const productSQs: ProductSizeQuantityMap = {
      ...(info?.doorOrderSizeQuantities ?? {}),
      // Note that these drop `id` from the original data
      ...mapValues(modifiedSizeQuantities, (quantity, sizeId) => ({ sizeId, quantity }))
    }
    info.doorOrderSizeQuantities = productSQs

    // Update product draft and total values
    info.doorOrderProductDoorQuantity = doorQuantity
    if (!isDoorOrder) {
      product.doorOrderProductDoorQuantity += doorQuantity - oldTotalDoorQuantity // optimistic
    }

    // Total falls back to door quantity when capture size is disabled or no sizes
    let sizeQuantityTotal: number
    if (isCaptureSizeEnabled && this.getSizes(product.id).length) {
      sizeQuantityTotal = isEmpty(productSQs)
        ? 0
        : reduce(productSQs, (acc, entry) => acc + +entry.quantity, 0)
    } else {
      sizeQuantityTotal = doorQuantity
    }
    info.doorOrderProductSizeQuantity = sizeQuantityTotal
    if (!isDoorOrder) {
      product.doorOrderProductSizeQuantity += sizeQuantityTotal - oldTotalSizeQuantity // optimistic
    }

    let sizeQuantities: QuantityPatchMap = mapValues(productSQs, entry => entry.quantity)

    const payload = {
      doorOrderId,
      isDoorOrder,
      draftQuantity: doorQuantity,
      sizeQuantity: sizeQuantityTotal,
      sizeQuantities: this.stripZeroQuantities(sizeQuantities)
    }

    return this.dataStore.updateDoorOrderDraftQuantity(productId, payload, product)
  }

  // -- Helpers --

  private stripZeroQuantities = (
    sizeQuantities: Record<string, number>
  ): Record<string, number> =>
    Object.fromEntries(Object.entries(sizeQuantities).filter(([, val]) => !!val))

  private getTemplateProductSizesValuesMap = (productSizes, doorOrderSizeQuantities) => {
    const tempDoorOrderSizeQuantities = {}
    productSizes.forEach(sizeElem => {
      const sizeId = sizeElem?.id
      if (sizeId) {
        tempDoorOrderSizeQuantities[sizeId] = {
          quantity: doorOrderSizeQuantities?.[sizeId]?.quantity ?? 0
        }
      }
    })
    return tempDoorOrderSizeQuantities
  }

  private mapToCopiedObject = (product, doorsOrdersAndDoors) => {
    const {
      queryParams: { orderType }
    } = nav
    const encodedProductImageUrl = getEnhancedImageURL(product)
    const productSizes = map(this.getSizes(product.id), size =>
      omitSingle('isInactive', size)
    )
    return {
      id: product.id,
      original: {
        externalImages: {
          img_Thumbnail: encodedProductImageUrl
            ? encodeURIComponent(encodedProductImageUrl)
            : null
        },
        style: {
          modelDescription: product?.['original-style-modelDescription']
        },
        defaultImage: product?.['original-defaultImage-id'],
        sizes: productSizes
      },
      quantity: {
        totalByDraftQuantity: product?.doorOrderProductDoorQuantity || 0,
        totalBySizeQuantity: product?.doorOrderProductSizeQuantity || 0,
        doorProducts: {
          ...(orderType === OrderType.Door
            ? {
                [doorsOrdersAndDoors[Object.keys(doorsOrdersAndDoors)[0]]]: {
                  totalByDraftQuantity: product?.doorOrderProductDoorQuantity || 0,
                  totalBySizeQuantity: product?.doorOrderProductSizeQuantity || 0,
                  sizeQuantities: this.getTemplateProductSizesValuesMap(
                    productSizes,
                    product?.doorOrderSizeQuantities
                  )
                }
              }
            : Object.keys(product?.doorOrderProducts).reduce((acc, key) => {
                const {
                  doorOrderProductDoorQuantity,
                  doorOrderProductSizeQuantity,
                  doorOrderSizeQuantities
                } = product?.doorOrderProducts[key]
                return {
                  ...acc,
                  [doorsOrdersAndDoors[key]]: {
                    totalByDraftQuantity: doorOrderProductDoorQuantity || 0,
                    totalBySizeQuantity: doorOrderProductSizeQuantity || 0,
                    sizeQuantities: this.getTemplateProductSizesValuesMap(
                      productSizes,
                      doorOrderSizeQuantities
                    )
                  }
                }
              }, {}))
        }
      }
    }
  }

  protected mapFromCopiedObject = (copiedValue, doorNameAndDoorOrderMap) => {
    const {
      queryParams: { orderType }
    } = nav
    return {
      id: copiedValue.id,
      original: {
        externalImages: {
          img_Thumbnail: decodeURIComponent(
            copiedValue.original?.externalImages?.img_Thumbnail
          )
        },
        style: {
          modelDescription: copiedValue.original?.style.modelDescription
        },
        defaultImage: copiedValue?.original?.defaultImage,
        sizes: copiedValue?.original?.sizes
      },

      doorOrderProductDoorQuantity: copiedValue.quantity.totalByDraftQuantity,
      doorOrderProductSizeQuantity: copiedValue.quantity.totalBySizeQuantity,
      ...(orderType === OrderType.Client
        ? {
            doorOrderProducts: {
              ...Object.keys(copiedValue.quantity.doorProducts).reduce(
                (acc, doorName) => {
                  const { totalByDraftQuantity, totalBySizeQuantity, sizeQuantities } =
                    copiedValue.quantity.doorProducts[doorName]
                  return {
                    ...acc,
                    [doorNameAndDoorOrderMap[doorName]]: {
                      doorOrderProductDoorQuantity: totalByDraftQuantity,
                      doorOrderProductSizeQuantity: totalBySizeQuantity,
                      doorOrderSizeQuantities: mapValues(
                        sizeQuantities,
                        ({ quantity }, sizeId) => ({
                          quantity,
                          sizeId
                        })
                      )
                    }
                  }
                },
                {}
              )
            }
          }
        : {
            doorOrderProductDoorQuantity:
              copiedValue.quantity.doorProducts[Object.keys(doorNameAndDoorOrderMap)[0]]
                .totalByDraftQuantity || 0,
            doorOrderProductSizeQuantity:
              copiedValue.quantity.doorProducts[Object.keys(doorNameAndDoorOrderMap)[0]]
                .totalBySizeQuantity || 0,
            doorOrderSizeQuantities: mapValues(
              copiedValue.quantity.doorProducts[Object.keys(doorNameAndDoorOrderMap)[0]]
                .sizeQuantities,
              ({ quantity }, sizeId) => ({
                quantity,
                sizeId
              })
            )
          })
    }
  }

  protected getDataToPaste = (
    DOOR_BOOKING,
    QUANTITIES,
    templateDoorOrderProducts,
    destinationDoorOrderProducts,
    doorIdAndOrderIdMap
  ) => {
    let doorOrderIdsToBook = []
    let doorOrderIdsToUnBook = []

    let doorOrderProductsToBeUpdated = templateDoorOrderProducts
    if (DOOR_BOOKING) {
      doorOrderIdsToBook = map(
        difference(keys(templateDoorOrderProducts), keys(destinationDoorOrderProducts)),
        id => doorIdAndOrderIdMap[id]
      )
      doorOrderIdsToUnBook = map(
        difference(keys(destinationDoorOrderProducts), keys(templateDoorOrderProducts)),
        id => doorIdAndOrderIdMap[id]
      )
    } else {
      doorOrderProductsToBeUpdated = destinationDoorOrderProducts
    }
    if (!QUANTITIES) {
      doorOrderProductsToBeUpdated = {}
    }
    return { doorOrderIdsToBook, doorOrderIdsToUnBook, doorOrderProductsToBeUpdated }
  }

  @action
  copyTemplate = (productId: string, { name, description = '' }, doorOrdersAndDoors) => {
    const product = this.dataStore.itemIndexObjectStore.idObjectMap.get(productId)
    const doorOrderAndDoorNameMap = doorOrdersAndDoors.reduce(
      (acc, doorOrder) => ({
        ...acc,
        [doorOrder.door.id]: doorOrder.door.name
      }),
      {}
    )
    const templatePreferences = orderTemplatePreferencesStore.templates

    const payloadToSave = {
      [OrderTemplatePreferences.TEMPLATES_PREFERENCE]: {
        clipboard: {
          quantity: {
            ...templatePreferences?.clipboard?.quantity,
            quantities: [
              {
                copyMetaData: { name, description },
                ...this.mapToCopiedObject(product, doorOrderAndDoorNameMap)
              },
              ...(templatePreferences?.clipboard?.quantity?.quantities || [])
            ]
          }
        }
      }
    }
    orderTemplatePreferencesStore.updatedOrderPreferences(payloadToSave)
  }

  @action
  pasteTemplateQuantity = (
    summary: any,
    productId: string,
    name: string,
    criteria?: TemplatePasteType
  ) => {
    const { DOOR_BOOKING, QUANTITIES } = criteria
    const {
      queryParams: { orderType }
    } = nav
    const productToPasteIn =
      this.dataStore.itemIndexObjectStore.idObjectMap.get(productId)
    const templatePreferences =
      orderTemplatePreferencesStore.templates?.clipboard?.quantity

    let selectedTemplateQuantity = templatePreferences?.['default']
    if (name !== 'default') {
      selectedTemplateQuantity = find(templatePreferences?.['quantities'], [
        'copyMetaData.name',
        name
      ])
    }
    if (!selectedTemplateQuantity) {
      return
    }
    const doorOrdersAndDoors = summary.doorOrder
      ? [{ id: summary.doorOrder.doorOrderId, door: summary.door }]
      : summary.doorOrders
    const doorNameAndDoorOrderMap = doorOrdersAndDoors.reduce(
      (acc, doorOrder) => ({
        ...acc,
        [doorOrder.door.name]: doorOrder?.door?.id
      }),
      {}
    )

    const doorIdAndOrderIdMap = doorOrdersAndDoors.reduce(
      (acc, doorOrder) => ({
        ...acc,
        [doorOrder.door.id]: doorOrder.id
      }),
      {}
    )

    const doorOrderIdAndDoorIdMap = doorOrdersAndDoors.reduce(
      (acc, doorOrder) => ({
        ...acc,
        [doorOrder.id]: doorOrder.door.id
      }),
      {}
    )
    const cleanedQuantity: any = this.mapFromCopiedObject(
      selectedTemplateQuantity,
      doorNameAndDoorOrderMap
    )

    let templateDoorOrderProducts = cleanedQuantity?.doorOrderProducts ?? {}
    let destinationDoorOrderProducts = productToPasteIn?.doorOrderProducts ?? {}

    const { doorOrderIdsToBook, doorOrderIdsToUnBook, doorOrderProductsToBeUpdated } =
      this.getDataToPaste(
        DOOR_BOOKING,
        QUANTITIES,
        templateDoorOrderProducts,
        destinationDoorOrderProducts,
        doorIdAndOrderIdMap
      )

    const doorIdsToUnBook = map(doorOrderIdsToUnBook, id => doorOrderIdAndDoorIdMap[id])
    const doorIdsToBook = map(doorOrderIdsToBook, id => doorOrderIdAndDoorIdMap[id])

    const sizes = this.getSizes(productId)
    let mergedDoorOrderProducts = null
    if (orderType === OrderType.Door) {
      mergedDoorOrderProducts = {
        [`${Object.values(doorNameAndDoorOrderMap)[0]}`]: {
          doorOrderProductDoorQuantity:
            cleanedQuantity?.doorOrderProductDoorQuantity ||
            productToPasteIn?.doorOrderProductDoorQuantity,
          doorOrderSizeQuantities: map(sizes, ({ id, isInactive }) => ({
            sizeId: id,
            ...(isInactive
              ? {
                  sizeId: id,
                  quantity: productToPasteIn?.doorOrderSizeQuantities?.[id]?.quantity || 0
                }
              : !isNil(cleanedQuantity?.doorOrderSizeQuantities?.[id]?.quantity)
              ? {
                  sizeId: id,
                  quantity: cleanedQuantity?.doorOrderSizeQuantities?.[id]?.quantity || 0
                }
              : {
                  sizeId: id,
                  quantity: productToPasteIn?.doorOrderSizeQuantities?.[id]?.quantity || 0
                })
          }))
        }
      }
    } else {
      mergedDoorOrderProducts = mapValues(
        doorOrderProductsToBeUpdated,
        (originalDoorOrder, doorId) => ({
          ...originalDoorOrder,
          ...templateDoorOrderProducts?.[doorId],
          doorOrderSizeQuantities: map(sizes, ({ id, isInactive }) => ({
            sizeId: id,
            ...(isInactive
              ? {
                  sizeId: id,
                  quantity:
                    destinationDoorOrderProducts?.[doorId]?.doorOrderSizeQuantities?.[id]
                      ?.quantity || 0
                }
              : !isNil(
                  templateDoorOrderProducts?.[doorId]?.doorOrderSizeQuantities?.[id]
                    ?.quantity
                )
              ? {
                  sizeId: id,
                  quantity:
                    templateDoorOrderProducts?.[doorId]?.doorOrderSizeQuantities?.[id]
                      ?.quantity || 0
                }
              : {
                  sizeId: id,
                  quantity:
                    destinationDoorOrderProducts?.[doorId]?.doorOrderSizeQuantities?.[id]
                      ?.quantity || 0
                })
          }))
        })
      )
    }
    const isCaptureSizeEnabled =
      summary?.client?.captureSize || summary?.clientOrder?.client?.captureSize
    const payload = {
      doorOrderId: orderType === OrderType.Door ? summary?.doorOrder?.doorOrderId : '',
      doorsToBook: { doorOrderIds: doorOrderIdsToBook },
      doorsToUnBook: { doorOrderIds: doorOrderIdsToUnBook },
      bulkDoorOrderQuantities: filter(
        map(Object.keys(mergedDoorOrderProducts), doorId => {
          const { doorOrderSizeQuantities, doorOrderProductDoorQuantity } =
            mergedDoorOrderProducts[doorId]
          let sizeQuantities = {}
          doorOrderSizeQuantities.forEach(size => {
            const { sizeId, quantity } = size
            sizeQuantities[sizeId] = quantity
          })
          return {
            doorOrderId: find(
              doorOrdersAndDoors,
              doorOrder => doorOrder?.door?.id === doorId
            )?.id,
            draftQuantity: doorOrderProductDoorQuantity,
            sizeQuantity:
              isEmpty(sizeQuantities) || !isCaptureSizeEnabled
                ? doorOrderProductDoorQuantity
                : sum(Object.values(sizeQuantities)),
            sizeQuantities
          }
        }),
        doorProduct =>
          !find(
            doorOrdersAndDoors,
            doorOrder => doorOrder?.id === doorProduct?.doorOrderId
          )?.deleted
      ),
      aggregations: {
        heirarchy: {
          metricAttributes: METRIC_ATTRIBUTES_FOR_DOOR_AND_SIZE_QUANTITY,
          ...this.dataStore.getPayloadForUpdatedPageLevelHeirarchy(productToPasteIn)
        },
        totalDoorHierarchyKPIs: {
          metricAttributes: METRIC_ATTRIBUTES_FOR_DOOR_AND_SIZE_QUANTITY,
          ...this.dataStore.getPayloadForUpdatedDoorLevelHeirarchy()
        },
        orderTopbarKPIs: {
          metricAttributes: KPI_TOPBAR_METRIC_ATTRIBUTES,
          groupBy: [
            orderType === OrderType.Door
              ? DOOR_ORDER_LEVEL_GROUP_ATTR
              : CLIENT_ORDER_LEVEL_GROUP_ATTR
          ]
        },
        ...this.dataStore.getPayloadForSingleProductAggregation(productId)
      }
    }
    return this.dataStore.pasteTemplate(
      summary,
      productId,
      payload,
      doorIdsToUnBook,
      doorIdsToBook
    )
  }

  @action
  deleteCopiedTemplate = name => {
    const quantities =
      orderTemplatePreferencesStore.templates?.clipboard?.quantity?.quantities || []

    const updatedQuantities =
      differenceBy(quantities, [{ copyMetaData: { name } }], 'copyMetaData.name') || []

    const payloadToSave = {
      [OrderTemplatePreferences.TEMPLATES_PREFERENCE]: {
        clipboard: {
          quantity: {
            ...orderTemplatePreferencesStore.templates?.clipboard?.quantity,
            quantities: updatedQuantities
          }
        }
      }
    }
    orderTemplatePreferencesStore.updatedOrderPreferences(payloadToSave)
  }

  @action
  updateAllProductBookingsForDoor = (doorOrderId, isUnbookAllOperation) => {
    const { orderType } = nav.queryParams
    if (orderType === OrderType.Client) {
      this.dataStore.updateAllProductBookingsForDoor(doorOrderId, isUnbookAllOperation)
    }
  }
}
